import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { UserApi } from "../apis";

const DATE_FMT = "YYYY-MM-DD";

/**
 * Fetches daily time-entry rows from the report endpoint and derives everything
 * the dashboards chart.
 *
 * The endpoint returns flat rows when `view: "daily"` is passed:
 *   { date, userId, user, project, phase, hours, description, status }
 *
 * All aggregation happens here so the dashboards stay presentational and the
 * backend needs no new endpoint.
 */
export function useTimeAnalytics({
  days = 30,
  userId = null,
  // Explicit bounds win over `days`, so a dashboard can offer a custom range
  // without the preset having to model it.
  from = null,
  to = null,
} = {}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const endDate = useMemo(
    () => (to ? dayjs(to).format(DATE_FMT) : dayjs().format(DATE_FMT)),
    [to]
  );
  const startDate = useMemo(
    () =>
      from
        ? dayjs(from).format(DATE_FMT)
        : dayjs(endDate).subtract(days - 1, "day").format(DATE_FMT),
    [from, days, endDate]
  );

  // Charts that bucket by calendar day need the real span, which is no longer
  // the same as `days` once a custom range is in play.
  const spanDays = useMemo(
    () => Math.max(1, dayjs(endDate).diff(dayjs(startDate), "day") + 1),
    [startDate, endDate]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { view: "daily", startDate, endDate };
      if (userId) params.userId = String(userId);

      const res = await UserApi.getReportByProject({ params });
      const data = Array.isArray(res?.data?.data) ? res.data.data : [];
      setRows(data.filter((r) => r && r.date));
    } catch (err) {
      console.error("Failed to load time analytics:", err);
      setError(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const analytics = useMemo(() => {
    const sumBy = (key) => {
      const map = new Map();
      rows.forEach((row) => {
        const name = row[key] || "Unassigned";
        map.set(name, (map.get(name) || 0) + Number(row.hours || 0));
      });
      return [...map.entries()]
        .map(([name, hours]) => ({ name, hours: Math.round(hours * 100) / 100 }))
        .sort((a, b) => b.hours - a.hours);
    };

    // Dense daily series — gaps must read as zero, not as missing points.
    const dayTotals = new Map();
    rows.forEach((row) => {
      dayTotals.set(row.date, (dayTotals.get(row.date) || 0) + Number(row.hours || 0));
    });

    const byDay = [];
    for (let i = 0; i < spanDays; i += 1) {
      const date = dayjs(startDate).add(i, "day").format(DATE_FMT);
      byDay.push({ date, hours: Math.round((dayTotals.get(date) || 0) * 100) / 100 });
    }

    const totalHours = byDay.reduce((acc, d) => acc + d.hours, 0);

    // Top projects, with the tail folded into a single "Other" bar.
    const allProjects = sumBy("project");
    const TOP_N = 7;
    const byProject =
      allProjects.length > TOP_N
        ? [
            ...allProjects.slice(0, TOP_N),
            {
              name: "Other",
              hours:
                Math.round(
                  allProjects.slice(TOP_N).reduce((acc, p) => acc + p.hours, 0) * 100
                ) / 100,
            },
          ]
        : allProjects;

    const statusMap = new Map();
    rows.forEach((row) => {
      const status = (row.status || "unknown").toLowerCase();
      statusMap.set(status, (statusMap.get(status) || 0) + Number(row.hours || 0));
    });
    const byStatus = [...statusMap.entries()]
      .map(([status, hours]) => ({ status, hours: Math.round(hours * 100) / 100 }))
      .sort((a, b) => b.hours - a.hours);

    const weekStart = dayjs().startOf("week");
    const thisWeekHours = byDay
      .filter((d) => !dayjs(d.date).isBefore(weekStart, "day"))
      .reduce((acc, d) => acc + d.hours, 0);

    const daysLogged = byDay.filter((d) => d.hours > 0).length;

    return {
      byDay,
      byProject,
      byUser: sumBy("user"),
      byPhase: sumBy("phase"),
      byStatus,
      totalHours: Math.round(totalHours * 100) / 100,
      thisWeekHours: Math.round(thisWeekHours * 100) / 100,
      daysLogged,
      // Average across days actually worked, not calendar days — otherwise
      // weekends drag the figure down and it stops being useful.
      avgPerLoggedDay: daysLogged ? Math.round((totalHours / daysLogged) * 100) / 100 : 0,
      activeProjects: allProjects.length,
      entryCount: rows.length,
    };
  }, [rows, spanDays, startDate]);

  return { ...analytics, rows, loading, error, reload: load, startDate, endDate, spanDays };
}

export default useTimeAnalytics;
